// Tipos e helpers de status compartilhados entre RotasView e RotaExecucaoCard.

import { googleMapsNavUrl, googleMapsNavUrlEndereco } from '../../../src/lib/routeOptimizer';

export interface Parada {
    id: string;
    posicao: number;
    status: string; // pendente | em_visita | visitado | pulado
    checkin_em?: string | null;
    checkout_em?: string | null;
    descricao_visita?: string | null;
    status_visita?: string | null;
    proxima_acao?: string | null;
    foto_url?: string | null;
    modalidade?: string | null;
    negociacao_id?: number | null;
    tarefa_id?: number | null;
    /** Tarefa que originou a parada. `tipo = 'pos_venda'` liga os chips de resultado na conclusão. */
    tarefas?: { tipo?: string | null } | null;
    negociacoes?: {
        id: number; titulo: string; latitude?: number | null; longitude?: number | null;
        cidade?: string | null; bairro?: string | null; segmento?: string | null;
        consumo_medio_kwh?: number | null; whatsapp?: string | null;
        /** endereco | bairro | cidade | pendente | falhou — quanto vale latitude/longitude. Ver `urlNavegacao`. */
        geocode_status?: string | null;
        // Endereço da(s) UC(s) — fallback quando a negociação não tem cidade/bairro,
        // mas a UC tem CEP/logradouro. Corrige o "Sem endereço" indevido nas rotas.
        unidades_consumidoras?: Array<{ logradouro?: string | null; numero?: string | null; bairro?: string | null; cidade?: string | null; cep?: string | null }> | null;
    } | null;
}

type NegEndereco = NonNullable<Parada['negociacoes']>;

/**
 * Endereço exibível de uma parada: usa bairro/cidade da negociação; se vazios,
 * cai para a 1ª UC (logradouro/número, bairro, cidade, CEP). Assim uma UC com CEP
 * deixa de aparecer como "Sem endereço".
 */
export const enderecoParada = (neg?: NegEndereco | null): string => {
    const daNeg = [neg?.bairro, neg?.cidade].filter(Boolean).join(', ');
    if (daNeg) return daNeg;
    const uc = neg?.unidades_consumidoras?.find(
        (u) => u && (u.logradouro || u.bairro || u.cidade || u.cep),
    );
    if (!uc) return '';
    const rua = [uc.logradouro, uc.numero].filter(Boolean).join(', ');
    return [rua, uc.bairro, uc.cidade, uc.cep].filter(Boolean).join(' · ');
};

/**
 * Link de navegação da parada, ou '' quando não há para onde mandar ninguém.
 *
 * A coordenada só é usada quando ela é do ENDEREÇO do cliente. Nos outros níveis
 * (`bairro`, `cidade`, `pendente`, `falhou`) ela é uma aproximação — no nível
 * 'cidade' é o centro do município — e navegar até lá leva o vendedor para o lugar
 * errado com cara de certeza. Nesses casos vai o endereço em texto, que o Google
 * resolve na hora e que o vendedor consegue conferir com os próprios olhos.
 *
 * Está aqui, e não em cada tela, porque a decisão se repetia em seis pontos entre
 * Rotas, Operador e o card de execução — e uma regra dessas só vale se valer nos seis.
 */
export const urlNavegacao = (neg?: NegEndereco | null): string => {
    if (!neg) return '';
    const temCoord = neg.latitude != null && neg.longitude != null;
    if (temCoord && neg.geocode_status === 'endereco') {
        return googleMapsNavUrl({ lat: neg.latitude!, lng: neg.longitude! });
    }
    const uc = neg.unidades_consumidoras?.find((u) => u && (u.logradouro || u.cidade));
    // Mesma regra do `prepararConsulta` da edge geocode-leads, na versão curta: o
    // `logradouro` da UC vem da fatura da Equatorial com quadra, lote, S/N e
    // complemento grudados ("RUA C-217, Q. 515, L. 14, S/N, CASA - 2"), e mandar
    // isso inteiro é o que faz o geocoder não achar. Só o trecho antes da 1ª
    // vírgula é nome de rua. Idem o número: 'S/N' não é número de porta.
    const rua = (uc?.logradouro || '').split(',')[0].trim();
    const numero = /^\d+[A-Za-z]?$/.test((uc?.numero || '').trim()) ? uc!.numero!.trim() : '';
    const endereco = [
        [rua, numero].filter(Boolean).join(', '),
        uc?.bairro,
        uc?.cidade || neg.cidade,
    ].filter(Boolean).join(', ');
    if (endereco) return googleMapsNavUrlEndereco(`${endereco}, Brasil`);
    // Sem endereço utilizável, a coordenada aproximada ainda é melhor que nada —
    // chega perto o suficiente para o vendedor ligar e perguntar o resto.
    return temCoord ? googleMapsNavUrl({ lat: neg.latitude!, lng: neg.longitude! }) : '';
};

export interface RotaAtiva {
    id: string;
    data: string;
    status: string;
    origem: string;
    vendedor_id?: string | null;
    nome?: string | null;
    prioridade?: string | null;
    observacoes?: string | null;
    distancia_total_km?: number | null;
    duracao_min?: number | null;
    gerada_em?: string | null;
    snapshot_otimizacao?: { versao?: number; origin?: { lat: number; lng: number } | null } | null;
    rota_paradas: Parada[];
}

// Concessão nominal (Config > Permissões Especiais): libera o seletor de
// vendedores e a geração de rota em nome de terceiros para quem não é
// adm/gerente — sem abrir as abas de gestão (Produtividade/Capturas/
// Aderência/Operacional), que continuam restritas a isManager.
export const GERAR_ROTA_TERCEIROS_PERMISSION = 'Gerar rota para outros vendedores';

// ── Meta comercial diária (adendo Bruno §4) ──
// 12 visitas CONCLUÍDAS por dia é a meta; planejar 15 dá margem p/ cancelamentos.
export const META_CONCLUIDAS_DIA = 12;
export const META_PLANEJADAS_DIA = 15;
/** A partir desta hora local, o aviso "Faltam X para a meta" fica mais insistente. */
export const HORA_CORTE_META = 15;

/** Cor da meta: verde ≥12 concluídas · âmbar 10-11 · vermelho <10. */
export const metaChipClass = (concluidas: number) =>
    concluidas >= META_CONCLUIDAS_DIA
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : concluidas >= META_CONCLUIDAS_DIA - 2
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

export const fmtDuracao = (min?: number | null) => {
    if (!min) return '';
    const h = Math.floor(min / 60);
    return h > 0 ? `${h}h${String(Math.round(min % 60)).padStart(2, '0')}` : `${Math.round(min)} min`;
};

export const statusRotaBadge = (s: string) => {
    switch (s) {
        case 'concluida': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        case 'em_execucao': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        case 'cancelada': return 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
        case 'agendada': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
        case 'reagendada': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
        default: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
};

export const statusRotaLabel = (s: string) => ({
    planejada: 'Planejada', agendada: 'Agendada', em_execucao: 'Em execução',
    concluida: 'Concluída', cancelada: 'Cancelada', reagendada: 'Reagendada'
}[s] || s);
